import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import type { VehicleLog } from '@container-system/shared';
import { vehicleLogsApi } from '../../api/vehicleLogs';
import { useAuth } from '../../app/AuthContext';
import { Button } from '../../components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '../../components/ui/Card';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, Pagination, type Column } from '../../components/ui/DataTable';
import { Input } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { PageHeader } from '../../components/ui/PageHeader';
import { FullPageSpinner } from '../../components/ui/Spinner';
import { useToast } from '../../components/ui/Toast';
import { ApiRequestError } from '../../api/client';
import { formatDate } from '../../utils/format';

const DEFAULT_VEHICLE_CODE = 'KOEV125';

export function VehicleLogPage() {
  const { hasRole } = useAuth();
  const canWrite = hasRole('ADMIN', 'OPERATOR');
  const toast = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<VehicleLog | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VehicleLog | null>(null);

  const query = useQuery({ queryKey: ['vehicle-logs', page], queryFn: () => vehicleLogsApi.list(page, 25) });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => vehicleLogsApi.remove(id),
    onSuccess: () => {
      toast.show('Eintrag gelöscht.', 'success');
      setDeleteTarget(null);
      void queryClient.invalidateQueries({ queryKey: ['vehicle-logs'] });
    },
  });

  const columns: Column<VehicleLog>[] = [
    { key: 'logDate', header: 'Datum', render: (l) => formatDate(l.logDate) },
    { key: 'vehicleCode', header: 'Fahrzeug', render: (l) => l.vehicleCode },
    { key: 'time', header: 'Zeit', render: (l) => `${l.startTime} – ${l.endTime}` },
    { key: 'km', header: 'km', render: (l) => l.kilometers.toLocaleString('de-DE') },
    { key: 'kwh', header: 'kWh', render: (l) => l.kwhConsumed.toLocaleString('de-DE') },
    ...(canWrite
      ? [
          {
            key: 'actions',
            header: '',
            render: (l: VehicleLog) => (
              <div className="flex justify-end gap-2">
                <button className="text-xs text-accent-400 hover:underline" onClick={() => setEditing(l)}>
                  Bearbeiten
                </button>
                <button className="text-xs text-danger-500 hover:underline" onClick={() => setDeleteTarget(l)}>
                  Löschen
                </button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Fahrzeug-Log"
        actions={
          canWrite && (
            <Button onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" strokeWidth={2} /> Eintrag
            </Button>
          )
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Fahrten</CardTitle>
        </CardHeader>
        <CardBody>
          {query.isLoading ? (
            <FullPageSpinner />
          ) : (
            <>
              <DataTable columns={columns} rows={query.data?.items ?? []} rowKey={(l) => l.id} emptyMessage="Keine Fahrten erfasst." />
              <Pagination page={page} totalPages={query.data?.totalPages ?? 1} onChange={setPage} />
            </>
          )}
        </CardBody>
      </Card>

      {editing && <VehicleLogFormModal initial={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}

      <ConfirmDialog
        open={!!deleteTarget}
        title="Eintrag löschen"
        message="Diesen Fahrtenbuch-Eintrag unwiderruflich löschen?"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function VehicleLogFormModal({ initial, onClose }: { initial: VehicleLog | null; onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [vehicleCode, setVehicleCode] = useState(initial?.vehicleCode ?? DEFAULT_VEHICLE_CODE);
  const [logDate, setLogDate] = useState(initial?.logDate ?? new Date().toISOString().slice(0, 10));
  const [startTime, setStartTime] = useState(initial?.startTime ?? '08:00');
  const [endTime, setEndTime] = useState(initial?.endTime ?? '16:00');
  const [kilometers, setKilometers] = useState(String(initial?.kilometers ?? ''));
  const [kwhConsumed, setKwhConsumed] = useState(String(initial?.kwhConsumed ?? ''));

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload = {
        vehicleCode,
        logDate,
        startTime,
        endTime,
        kilometers: Number(kilometers.replace(',', '.')),
        kwhConsumed: Number(kwhConsumed.replace(',', '.')),
      };
      return initial ? vehicleLogsApi.update(initial.id, payload) : vehicleLogsApi.create(payload);
    },
    onSuccess: () => {
      toast.show('Gespeichert.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['vehicle-logs'] });
      onClose();
    },
    onError: (err) => toast.show(err instanceof ApiRequestError ? err.message : 'Speichern fehlgeschlagen.', 'error'),
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={initial ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input label="Fahrzeug-Kennung" value={vehicleCode} onChange={(e) => setVehicleCode(e.target.value)} />
        <Input label="Datum" type="date" value={logDate} onChange={(e) => setLogDate(e.target.value)} />
        <div className="grid grid-cols-2 gap-4">
          <Input label="Start" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
          <Input label="Ende" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Input label="Kilometer" type="number" step="0.1" value={kilometers} onChange={(e) => setKilometers(e.target.value)} />
          <Input label="kWh verbraucht" type="number" step="0.1" value={kwhConsumed} onChange={(e) => setKwhConsumed(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}
